<template>
    <div class="lmc">
        <div class="lmc-head">
            <span v-for="w in weekdayNames" :key="w">{{ w }}</span>
        </div>
        <div class="lmc-grid">
            <div
                v-for="(c, i) in cells"
                :key="i"
                class="lmc-cell"
                :class="{ blank: !c.day, weekend: c.day && c.day.isWeekend, today: c.day && c.day.isToday }"
                @click="c.day && $emit('day-click', c.day)"
            >
                <template v-if="c.day">
                    <div class="lmc-num">{{ c.day.dayNum }}</div>
                    <button
                        v-if="canAddLeave"
                        type="button"
                        class="lmc-add"
                        title="Add leave"
                        @click.stop="$emit('add-leave', c.day)"
                    >+</button>
                    <div v-if="c.people.length" class="lmc-people">
                        <div
                            v-for="p in c.people.slice(0, MAX_PEOPLE)"
                            :key="p.kind + p.request.id"
                            class="lmc-person"
                            :class="[p.kind, { half: p.request.is_half_day }]"
                            :style="personStyle(p)"
                            :title="personTitle(p)"
                        >
                            <span v-if="p.kind === 'wfh'" class="lmc-glyph">⌂</span>{{ shortName(p.userId) }}<span v-if="p.request.is_half_day" class="lmc-half"> ½</span>
                        </div>
                        <div v-if="c.people.length > MAX_PEOPLE" class="lmc-more" :title="names(c.people)">
                            +{{ c.people.length - MAX_PEOPLE }} more
                        </div>
                    </div>
                    <div v-for="n in c.notes.slice(0, 2)" :key="n.id" class="lmc-note" :class="{ general: !n.user_id }" :title="noteTitle(n)">
                        <span v-if="n.is_private" aria-label="Private">🔒 </span>{{ n.user_id ? userName(n.user_id) + ': ' : '' }}{{ n.body }}
                    </div>
                    <div v-if="c.notes.length > 2" class="lmc-more">+{{ c.notes.length - 2 }} more</div>
                </template>
            </div>
        </div>
        <div class="lmc-legend">
            <span v-for="lt in leaveTypes" :key="lt.id" class="lmc-lg">
                <i :style="{ backgroundColor: colorForType(lt.id) }"></i>{{ lt.name }}
            </span>
            <span class="lmc-lg"><i class="lmc-sw-pending"></i> Pending approval</span>
            <span><i class="lmc-sw-note"></i> Note (red text = whole {{ scopeLabel }})</span>
            <span class="grey--text">Click a day for details</span>
        </div>
    </div>
</template>

<script>
import { fullName } from './leaveUtils';

// Month-grid ("global") calendar: per day, how many are on leave / WFH / pending,
// plus the managers' notes. Pure display; the panel owns the data and the dialogs.
export default {
    name: 'LeaveMonthCalendar',
    props: {
        days: { type: Array, required: true }, // buildDays() output for the month
        buckets: { type: Object, default: () => ({}) }, // buildDayBuckets() output
        notes: { type: Array, default: () => [] },
        users: { type: Array, default: () => [] },
        scopeLabel: { type: String, default: 'company' },
        leaveTypes: { type: Array, default: () => [] },
        colorForType: { type: Function, required: true },
        // show a hover "+" on each day to log leave for someone
        canAddLeave: { type: Boolean, default: false },
    },
    data() {
        return { weekdayNames: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'], MAX_PEOPLE: 4 };
    },
    computed: {
        notesByDay() {
            const m = {};
            this.notes.forEach((n) => {
                const k = (n.note_date || '').substring(0, 10);
                (m[k] = m[k] || []).push(n);
            });
            return m;
        },
        cells() {
            if (!this.days.length) return [];
            const lead = (this.days[0].dow + 6) % 7; // Monday-first
            const out = Array.from({ length: lead }, () => ({ day: null }));
            this.days.forEach((d) => {
                const b = this.buckets[d.key];
                const people = b
                    ? [
                        ...b.leave.map((e) => ({ ...e, kind: 'leave' })),
                        ...b.wfh.map((e) => ({ ...e, kind: 'wfh' })),
                        ...b.pending.map((e) => ({ ...e, kind: 'pending' })),
                    ]
                    : [];
                out.push({ day: d, people, notes: this.notesByDay[d.key] || [] });
            });
            return out;
        },
    },
    methods: {
        userName(id) {
            const u = this.users.find((x) => Number(x.id) === Number(id));
            return u ? fullName(u) : 'Unknown';
        },
        shortName(id) {
            const u = this.users.find((x) => Number(x.id) === Number(id));
            return u ? u.first_name || u.name : 'Unknown';
        },
        personStyle(p) {
            const color = this.colorForType(p.request.leave_type_id);
            if (p.kind === 'wfh') return { color, borderColor: color, background: 'transparent' };
            return { backgroundColor: color, borderColor: color };
        },
        personTitle(p) {
            const t = this.leaveTypes.find((x) => x.id === p.request.leave_type_id) || p.request.leave_type || {};
            const state = p.kind === 'pending' ? ' (pending approval)' : '';
            return `${this.userName(p.userId)} — ${t.name || 'Leave'}${state}`;
        },
        names(entries) {
            return entries.map((e) => this.userName(e.userId)).join(', ');
        },
        noteTitle(n) {
            return `${n.user_id ? this.userName(n.user_id) + ' — ' : ''}${n.body}${n.author ? ' (' + n.author.name + ')' : ''}`;
        },
    },
};
</script>

<style scoped>
.lmc-head,
.lmc-grid {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
}

.lmc-head span {
    text-align: center;
    font-size: 12px;
    font-weight: 600;
    color: #78909c;
    padding: 6px 0;
}

.lmc-grid {
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    border-left: 1px solid rgba(0, 0, 0, 0.1);
}

.lmc-add {
    position: absolute;
    top: 2px;
    right: 2px;
    width: 18px;
    height: 18px;
    line-height: 16px;
    border-radius: 50%;
    border: 1px solid #1976d2;
    background: #fff;
    color: #1976d2;
    font-size: 14px;
    font-weight: 700;
    padding: 0;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.12s;
    z-index: 2;
}

.lmc-cell:hover .lmc-add,
.lmc-add:focus {
    opacity: 1;
}

.lmc-cell {
    position: relative;
    min-height: 104px;
    padding: 4px 6px;
    border-right: 1px solid rgba(0, 0, 0, 0.1);
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    cursor: pointer;
    overflow: hidden;
}

.lmc-cell:hover {
    background: #f5f9ff;
}

.lmc-cell.weekend {
    background: #f2f2f2;
}

.lmc-cell.blank {
    background: transparent;
    cursor: default;
}

.lmc-num {
    font-size: 12px;
    font-weight: 600;
    color: #546e7a;
}

.lmc-cell.today .lmc-num {
    display: inline-block;
    min-width: 20px;
    text-align: center;
    border-radius: 10px;
    background: #1976d2;
    color: #fff;
}

.lmc-people {
    margin: 3px 0;
}

.lmc-person {
    font-size: 11px;
    font-weight: 600;
    line-height: 1.3;
    color: #fff;
    border: 1px solid transparent;
    border-radius: 3px;
    padding: 0 5px;
    margin-bottom: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* work from home: outlined (not solid), like the wallchart */
.lmc-person.wfh {
    font-weight: 700;
}

.lmc-glyph {
    margin-right: 3px;
}

/* waiting for approval: same colour, faded with a dashed edge */
.lmc-person.pending {
    opacity: 0.55;
    border-style: dashed;
    border-color: rgba(0, 0, 0, 0.55) !important;
}

.lmc-half {
    opacity: 0.9;
}

.lmc-lg {
    display: inline-flex;
    align-items: center;
}

.lmc-lg i {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 3px;
    margin-right: 5px;
}

.lmc-lg i.lmc-sw-pending {
    background: #9e9e9e;
    opacity: 0.55;
    border: 1px dashed rgba(0, 0, 0, 0.55);
}

.lmc-note {
    font-size: 11px;
    line-height: 1.25;
    color: #455a64;
    background: #fffde7;
    border-left: 3px solid #fbc02d;
    padding: 1px 4px;
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.lmc-note.general {
    color: #c62828;
    background: #ffebee;
    border-left-color: #e53935;
}

.lmc-more {
    font-size: 10px;
    color: #78909c;
    margin-top: 1px;
}

.lmc-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 16px;
    align-items: center;
    margin-top: 10px;
    font-size: 12px;
    color: #616161;
}

.lmc-sw-note {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-left: 3px solid #fbc02d;
    background: #fffde7;
    vertical-align: middle;
}
</style>
